import React from 'react';
import { NavLink, Routes, Route, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCapabilities } from '../../hooks/useCapabilities';
import {
    LayoutDashboard,
    Megaphone,
    Tag,
    Users,
    ArrowLeft,
    LogOut,
    Shield,
    BarChart2,
    MessageSquare,
    MessagesSquare,
    Sparkles,
    Key
} from 'lucide-react';
import Campaigns from './Campaigns';
import Offers from './Offers';
import AssignmentDragDrop from './AssignmentDragDrop';
import PermissionsGrid from './PermissionsGrid';
import AgentDashboard from './AgentDashboard';
import Roles from './Roles';
import TemplatesByRole from './TemplatesByRole';
import WhatsAppAnalytics from './WhatsAppAnalytics';
import WhatsAppSupervisorView from './WhatsAppSupervisorView';
import CatcherFields from './CatcherFields';
import WhatsAppMessageAdRules from './WhatsAppMessageAdRules';
import ApiKeys from './ApiKeys';
import AgencySettings from './AgencySettings';

const BackofficeLayout = () => {
    const { logout, agentInfo } = useAuth();
    const { hasCapability } = useCapabilities();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const allNavItems = [
        { path: '', label: 'Dashboard', icon: LayoutDashboard, exact: true, requiredCapability: 'backoffice' },
        { path: 'campaigns', label: 'Campañas', icon: Megaphone, requiredCapability: 'backoffice' },
        { path: 'offers', label: 'Ofertas', icon: Tag, requiredCapability: 'backoffice' },
        { path: 'assignments', label: 'Asignaciones D&D', icon: Users, requiredCapability: 'backoffice' },
        { path: 'permissions', label: 'Permisos', icon: Users, requiredCapability: 'backoffice' },
        { path: 'roles', label: 'Roles & Canales', icon: Shield, requiredCapability: 'backoffice' },
        { path: 'agencies', label: 'Agencias WhatsApp', icon: Users, requiredCapability: 'backoffice' },
        { path: 'templates', label: 'Templates por Rol', icon: MessagesSquare, requiredCapability: 'backoffice' },
        { path: 'whatsapp-analytics', label: 'WhatsApp Analytics', icon: BarChart2, requiredCapability: ['backoffice', 'whatsapp_admin'] },
        { path: 'whatsapp-conversations', label: 'Conversaciones WA', icon: MessageSquare, requiredCapability: ['backoffice', 'whatsapp_admin'] },
        { path: 'whatsapp-message-ad-rules', label: 'Reglas WhatsApp Ads', icon: Megaphone, requiredCapability: 'whatsapp_admin' },
        { path: 'catcher', label: 'Catcher', icon: Sparkles, requiredCapability: 'backoffice' },
        { path: 'api-keys', label: 'API Keys', icon: Key, requiredCapability: 'api_admin' },
    ];

    // Filter nav items based on user capabilities
    const navItems = allNavItems.filter(item => {
        const required = item.requiredCapability;
        if (Array.isArray(required)) {
            // If it's an array, user needs ANY of these capabilities (OR logic)
            return required.some(cap => hasCapability(cap));
        }
        return hasCapability(required);
    });

    return (
        <div className="flex h-screen bg-[#020817] text-slate-50 font-sans overflow-hidden">
            {/* Sidebar */}
            <aside className="w-64 bg-[#0f172a] border-r border-slate-800 flex flex-col justify-between hidden md:flex">
                <div>
                    <div className="p-6 border-b border-slate-800 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center border border-primary/30">
                            <LayoutDashboard size={18} className="text-primary" />
                        </div>
                        <div>
                            <h2 className="font-bold text-lg tracking-tight">Backoffice</h2>
                            <p className="text-xs text-slate-400">Panel de Control</p>
                        </div>
                    </div>

                    <nav className="p-4 space-y-2">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.path}
                                to={`/backoffice/${item.path}`}
                                end={item.exact}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${isActive
                                        ? 'bg-primary/10 text-primary border border-primary/20 shadow-sm'
                                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 border border-transparent'
                                    }`
                                }
                            >
                                <item.icon size={18} />
                                <span className="font-medium">{item.label}</span>
                            </NavLink>
                        ))}
                    </nav>
                </div>

                <div className="p-4 border-t border-slate-800 space-y-2">
                    <button
                        onClick={() => navigate('/')}
                        className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 rounded-xl transition-all duration-200"
                    >
                        <ArrowLeft size={18} />
                        <span className="font-medium">Volver a Consola</span>
                    </button>
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl transition-all duration-200"
                    >
                        <LogOut size={18} />
                        <span className="font-medium">Cerrar Sesión</span>
                    </button>

                    <div className="mt-4 px-4 py-2 bg-slate-900 rounded-lg border border-slate-800 text-xs text-slate-500 text-center">
                        Admin: {agentInfo?.full_name || agentInfo?.email}
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 flex flex-col min-w-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-black overflow-y-auto">
                <Routes>
                    {/* Full width route for WhatsApp Supervisor */}
                    <Route path="whatsapp-conversations" element={<div className="p-8 w-full h-full"><WhatsAppSupervisorView /></div>} />
                    
                    {/* Standard max-width routes */}
                    <Route path="*" element={
                        <div className="p-8 max-w-7xl mx-auto w-full">
                            <Routes>
                                <Route path="/" element={<AgentDashboard />} />
                                <Route path="campaigns" element={<Campaigns />} />
                                <Route path="offers" element={<Offers />} />
                                <Route path="assignments" element={<AssignmentDragDrop />} />
                                <Route path="permissions" element={<PermissionsGrid />} />
                                <Route path="roles" element={<Roles />} />
                                <Route path="agencies" element={hasCapability('backoffice') ? <AgencySettings /> : <div>Acceso no permitido.</div>} />
                                <Route path="templates" element={<TemplatesByRole />} />
                                <Route path="whatsapp-analytics" element={<WhatsAppAnalytics />} />
                                <Route
                                    path="whatsapp-message-ad-rules"
                                    element={hasCapability('whatsapp_admin') ? <WhatsAppMessageAdRules /> : <div className="p-8 text-slate-400">No tienes permiso para acceder a esta pantalla.</div>}
                                />
                                <Route path="catcher" element={<CatcherFields />} />
                                <Route
                                    path="api-keys"
                                    element={hasCapability('api_admin') ? <ApiKeys /> : <div className="p-8 text-slate-400">No tienes permiso para acceder a esta pantalla.</div>}
                                />
                            </Routes>
                        </div>
                    } />
                </Routes>
            </main>
        </div>
    );
};

export default BackofficeLayout;
